import ListSubheader from '@mui/material/ListSubheader';
import { FC, useEffect, useMemo } from 'react';
import { BadgeProps, Button, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { BaseButtonMenu } from '@mgm/common/components/base';
import { TBaseFilterMenu } from './type';
import { ReactComponent as FilterIcon } from '../../../static/icons/ic_filter.svg';
import { useFilter } from '../../../hooks/useFilter';
import { TItemChildren } from '../BList/type';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { commonActions } from '../../../store/common/slice';
import { getSelectedFilterItems } from '../../../store/common/selectors';

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -3,
    top: -4,
    fontSize: 10,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 2px',
  },
}));

const BaseFilterMenu: FC<TBaseFilterMenu> = ({ items, isDisplayLabel = true }) => {
  const { setSelectedItems, selectedItems, resetFilter } = useFilter();
  const selectedFilterItems = useAppSelector(getSelectedFilterItems);
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['common', 'team']);

  useEffect(() => {
    Object.entries(selectedFilterItems).forEach(([key, value]) => {
      setSelectedItems((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    });
  }, []);

  const handleItemChange = (child: TItemChildren, category: string) => {
    const items: Array<TItemChildren> = selectedItems[category];
    let data: Array<TItemChildren> = [];

    const isChildExists = items.find((item) => item.value === child.value);

    if (isChildExists) {
      data = items.filter((item) => item.value !== child.value);
    } else {
      data = [...items, child];
    }

    setSelectedItems((prevState) => ({
      ...prevState,
      [category]: data,
    }));
  };

  const handleSave = () => {
    dispatch(commonActions.selectedFilterItems({ ...selectedItems }));
  };

  const handleRemove = () => {
    resetFilter();
    dispatch(commonActions.selectedFilterItems({}));
  };

  const badgeContent = useMemo(() => {
    let filterLength = 0;

    Object.values(selectedFilterItems).forEach((item) => {
      filterLength += item.length;
    });

    return filterLength;
  }, [selectedFilterItems]);

  return (
    <BaseButtonMenu
      setSelectedItems={handleItemChange}
      selectedItems={selectedItems}
      buttonProps={{
        ...(isDisplayLabel ? {
          children: (
            <StyledBadge
              badgeContent={badgeContent}
              color="primary"
            >
              { t('filters') }
            </StyledBadge>
          ),
        } : null),
        sx: {
          '& .MuiButton-startIcon': {
            marginRight: isDisplayLabel ? '8px' : 0,
          },
        },
        startIcon: (
          <SvgIcon
            component={FilterIcon}
            inheritViewBox
          />
        ),
        size: 'small',
      }}
      listProps={{
        items,
        sx: { width: 400, pb: 1 },
        subheader: (
          <ListSubheader
            className="justify-space-between pb-2 pt-2"
            component="div"
            id="nested-list-subheader"
          >
            <Typography
              variant="h5"
              display="flex"
              alignItems="center"
            >
              {t('filters')}
            </Typography>
            <Box>
              <Button color="inherit" onClick={handleRemove}>{t('removeAll', { ns: 'team' })}</Button>
              <Button onClick={handleSave}>{t('saveView')}</Button>
            </Box>
          </ListSubheader>
        ),
      }}
    />
  );
};

export {
  BaseFilterMenu,
};
